import { matchesRouterPath } from "@/app/router/router-path";
import {
  ADMIN_FINANCE_ROLE_CLAIMS,
  ADMIN_OPERATOR_ROLE_CLAIMS,
} from "@/core/auth/admin-role-tiers";
import type { ModuleId, RoleClaim, RouteDefinition, RuntimeConfig } from "@/types/clm-web";

export const PUBLIC_ROUTES = [
  {
    id: "login",
    path: "/login",
    label: "Login",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    id: "auth-callback",
    path: "/auth/callback",
    label: "Authentication Callback",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    id: "logout",
    path: "/logout",
    label: "Logout",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    id: "error",
    path: "/error",
    label: "Error",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    id: "health",
    path: "/health",
    label: "Health",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    // CLMS-113: public invitation registration; the proof arrives only in
    // the URL fragment and is captured/removed by the feature route itself.
    id: "registration-invitation",
    path: "/register/invitation",
    label: "Invitation Registration",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    // 011-15 A §8.1 / 011-21 FR-050: post-login return target of the
    // credential-recovery journey. Carries only the opaque recovery
    // reference in `ref`, which the page scrubs on mount; the proof is
    // re-derived from the pasted invitation link, never from the URL.
    id: "registration-invitation-recovery",
    path: "/register/invitation/recovery",
    label: "Invitation Credential Recovery",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    // CLMS-137: standard-public first-credential page. The URL is always
    // bearer-free — the fc1 authorization arrives only through the one-read
    // in-memory handoff, never in a path, query, or fragment.
    id: "set-password",
    path: "/set-password",
    label: "Set Password",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    // CLMS-115 (D14): public application intake forms and received page.
    // Standard realm only (AppRouter); the admin realm falls through to the
    // fail-closed legacy entry below.
    id: "public-application-fellow",
    path: "/register/fellow",
    label: "Fellow Application",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    id: "public-application-member",
    path: "/register/member",
    label: "Member Application",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    id: "public-application-received",
    path: "/register/received",
    label: "Application Received",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    // CLMS-113 US4 (FR-025): legacy registration paths (composite-key
    // sections, reversible encrypted-ID routes) fail closed on a public
    // generic-unavailable page instead of bouncing through auth/dashboard.
    id: "registration-legacy",
    path: "/register/*",
    label: "Registration",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    // T152/T163: legacy /home/* site-content pages (privacy policy, terms,
    // antitrust, contact) are public in legacy and here; the AppRouter serves
    // them on both realms. This manifest is classification-only — AppRouter
    // declares its routes manually, so this entry generates no routes.
    id: "site-content",
    path: "/home/*",
    label: "Site Content",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    // 011-CLMS-241 FR-018: Live Preview target the CMS admin frames; public (it shows only what
    // the stage's CMS sends), noindex, and in no menu. Standard realm only (AppRouter).
    id: "cms-preview",
    path: "/cms-preview/:key",
    label: "Content preview",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    // 006-9 T056 (UI contract §2, §3.2): the invoice locator landing route is
    // public — the server decides between login hand-off, preselection, and
    // guidance. `pathMatches` treats a `:param` segment as one non-empty path
    // segment, so `/pay/i/<locator>` classifies as public without a wildcard
    // that would also admit deeper paths.
    id: "pay-locator-landing",
    path: "/pay/i",
    label: "Open payment link",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    id: "pay-locator-landing-locator",
    path: "/pay/i/:locator",
    label: "Open payment link",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
  {
    // 006-9 T056 (UI contract §4): neutral guidance for links that could not
    // be opened; renders for anonymous and authenticated visitors alike.
    id: "pay-guidance",
    path: "/pay/guidance",
    label: "Payment link guidance",
    public: true,
    requiredRoles: [],
    metadata: { allowWhenUnauthenticated: true },
  },
] as const satisfies readonly RouteDefinition[];

export const PROTECTED_ROUTES = [
  createProtectedRoute("dashboard", "/", "Dashboard", "dashboard", ["authenticated"]),
  createProtectedRoute("dashboard-index", "/dashboard", "Dashboard", "dashboard", [
    "authenticated",
  ]),
  createProtectedRoute("admin", "/admin", "Admin", "admin", [...ADMIN_OPERATOR_ROLE_CLAIMS]),
  // 006-9 UI contract §8a: staff invoice routes of the admin-realm financial
  // module, listed before the admin wildcard so `findProtectedRoute` names them.
  createProtectedRoute(
    "admin-financial-invoices",
    "/admin/financial/invoices",
    "Invoices",
    "financial",
    [...ADMIN_FINANCE_ROLE_CLAIMS],
  ),
  createProtectedRoute(
    "admin-financial-invoice-detail",
    "/admin/financial/invoices/:invoiceId",
    "Invoice detail",
    "financial",
    [...ADMIN_FINANCE_ROLE_CLAIMS],
  ),
  // 011-CLMS-237: CMS session hand-off; any operator tier (the CMS decides).
  createProtectedRoute("admin-content-handoff", "/admin/content/handoff", "Manage content", "cms", [
    ...ADMIN_OPERATOR_ROLE_CLAIMS,
  ]),
  createProtectedRoute("admin-wildcard", "/admin/*", "Admin", "admin", [
    ...ADMIN_OPERATOR_ROLE_CLAIMS,
  ]),
  createProtectedRoute("member", "/member", "Member", "member", ["member"]),
  createProtectedRoute("member-wildcard", "/member/*", "Member", "member", ["member"]),
  createProtectedRoute("financial", "/financial", "Financial", "financial", ["financial:read"]),
  createProtectedRoute("financial-wildcard", "/financial/*", "Financial", "financial", [
    "financial:read",
  ]),
  createProtectedRoute(
    "industry-resource-directory",
    "/industry-resource-directory",
    "Industry Resource Directory",
    "industry-resource-directory",
    ["directory:read"],
  ),
  createProtectedRoute(
    "industry-resource-directory-wildcard",
    "/industry-resource-directory/*",
    "Industry Resource Directory",
    "industry-resource-directory",
    ["directory:read"],
  ),
  createProtectedRoute("jobs", "/jobs", "Jobs", "jobs", ["jobs:read"]),
  createProtectedRoute("jobs-wildcard", "/jobs/*", "Jobs", "jobs", ["jobs:read"]),
  createProtectedRoute("ucc", "/ucc", "UCC", "ucc", ["ucc:read"]),
  createProtectedRoute("ucc-wildcard", "/ucc/*", "UCC", "ucc", ["ucc:read"]),
  createProtectedRoute("events", "/events", "Events", "events", ["events:read"]),
  createProtectedRoute("events-wildcard", "/events/*", "Events", "events", ["events:read"]),
  createProtectedRoute("courses", "/courses", "Courses", "courses", ["courses:view", "member"]),
  createProtectedRoute("courses-wildcard", "/courses/*", "Courses", "courses", [
    "courses:view",
    "member",
  ]),
  // 004-6 T048/T049: the learner's enrolments and purchases in progress; part
  // of the courses module so the restricted shell hides it with the module.
  createProtectedRoute("my-courses", "/my-courses", "My Courses", "courses", ["member"]),
  createProtectedRoute("compliance", "/compliance", "Compliance", "compliance", [
    "compliance:read",
  ]),
  createProtectedRoute("compliance-wildcard", "/compliance/*", "Compliance", "compliance", [
    "compliance:read",
  ]),
  createProtectedRoute("documents", "/documents", "Documents", "documents", ["documents:read"]),
  createProtectedRoute("documents-wildcard", "/documents/*", "Documents", "documents", [
    "documents:read",
  ]),
  createProtectedRoute("search", "/search", "Search", "search", ["search:read"]),
  createProtectedRoute("search-wildcard", "/search/*", "Search", "search", ["search:read"]),
  // 006-9 member invoice payment (UI contract §7/§5): any authenticated
  // standard-realm session, including `pre_activation` (standing contract §6.1).
  createProtectedRoute("pay-invoices", "/pay/invoices", "My invoices", "member", ["authenticated"]),
  createProtectedRoute("pay-invoices-wildcard", "/pay/invoices/*", "My invoices", "member", [
    "authenticated",
  ]),
] as const satisfies readonly RouteDefinition[];

export const ROUTE_MANIFEST = [
  ...PUBLIC_ROUTES,
  ...PROTECTED_ROUTES,
] as const satisfies readonly RouteDefinition[];

export function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTES.some((route) => pathMatches(route.path, pathname));
}

/** Manifest path for a route id; features link to the manifest, never to a copied literal. */
export function routePathById(id: string): RouteDefinition["path"] {
  const route = ROUTE_MANIFEST.find((candidate) => candidate.id === id);
  if (!route) {
    throw new Error(`Route "${id}" is missing from the route manifest.`);
  }
  return route.path;
}

export function findProtectedRoute(pathname: string): RouteDefinition | undefined {
  return PROTECTED_ROUTES.find((route) => pathMatches(route.path, pathname));
}

export function isProtectedRoute(pathname: string): boolean {
  return !isPublicRoute(pathname) && Boolean(findProtectedRoute(pathname));
}

export function getRouteRequiredRoles(pathname: string): RoleClaim[] {
  if (isPublicRoute(pathname)) {
    return [];
  }

  return findProtectedRoute(pathname)?.requiredRoles ?? ["authenticated"];
}

export function buildModulePath(routeBase: `/${string}`, childPath = ""): `/${string}` {
  const base = normalizePath(routeBase);
  const child = childPath.trim();

  if (!child || child === "/") {
    return base;
  }

  return normalizePath(`${base}/${child.replace(/^\/+/, "")}`);
}

export function buildAppUrl(
  config: Pick<RuntimeConfig, "appBaseUrl">,
  routePath: `/${string}` | "/",
): string {
  const base = new URL(config.appBaseUrl);
  base.pathname = buildModulePath("/", routePath);
  return base.toString();
}

export function createSubdomainReadyHref(
  config: Pick<RuntimeConfig, "appBaseUrl">,
  routePath: `/${string}` | "/",
  futureHostAlias?: string | null,
): string {
  if (!futureHostAlias) {
    return buildModulePath("/", routePath);
  }

  const url = new URL(buildAppUrl(config, routePath));
  url.host = futureHostAlias;
  return url.toString();
}

function createProtectedRoute(
  id: string,
  path: `/${string}` | "/",
  label: string,
  moduleId: ModuleId,
  requiredRoles: RoleClaim[],
): RouteDefinition {
  return {
    id,
    path,
    label,
    public: false,
    moduleId,
    requiredRoles,
    metadata: {
      denyByDefaultReason:
        "Protected routes require an authenticated session unless explicitly allowlisted.",
    },
  };
}

// The router's match (decoded, any letter case, `:param`, `/*`) on the slash-normalised pathname.
function pathMatches(routePath: `/${string}` | "/", pathname: string): boolean {
  return matchesRouterPath(routePath, normalizePath(pathname));
}

function normalizePath(path: string): `/${string}` {
  const normalized = `/${path}`.replace(/\/+/g, "/").replace(/\/$/, "");
  return (normalized === "" ? "/" : normalized) as `/${string}`;
}
